import SearchIcon from "@assets/icons/new/search_24px.svg?react";
import { SearchTextField } from "@components/common/search-text-field";
import { TypedEvent } from "@faro-lotv/foundation";
import { QUICK_FILTER } from "@hooks/table-filters/use-filter-query-params";
import { useDebouncedSearchText } from "@hooks/use-debounced-search-text";
import { useMediaQueryList } from "@hooks/use-media-query";
import {
  Box,
  ClickAwayListener,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import { setDebouncedSearchText } from "@store/ui/ui-slice";
import { EDecimalToHex, sphereColors } from "@styles/common-colors";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { addTransparency } from "@utils/ui-utils";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** Event to clear the table search */
export const onClearTableSearch = new TypedEvent<void>();

/** Handling the quick search for filtering the table */
export function FilterToolbarQuickSearch(): JSX.Element {
  const {
    isScreenXlOrLarger,
    isScreenSmOrLarger,
    isScreenSmOrSmaller,
    isExtraSmall,
  } = useMediaQueryList();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState<string>(searchParams.get(QUICK_FILTER) ?? "");
  const { debouncedSearchText }= useDebouncedSearchText({ searchText });
  const [shouldShowSearchBox, setShouldShowSearchBox] = useState<boolean>(searchText !== "");
  const [isTextFieldFocused, setIsTextFieldFocused] = useState<boolean>(false);
  const { trackEvent } = useTrackEvent();
  const tableType = useAppSelector(tableTypeSelector);
  const dispatch = useAppDispatch();

  // Sets the debounced search text in redux store
  useEffect(() => {
    dispatch(setDebouncedSearchText(debouncedSearchText));

    return () => {
      dispatch(setDebouncedSearchText(""));
    };
  }, [debouncedSearchText, dispatch]);

  const searchWidth = useMemo(() => {
    if (isScreenXlOrLarger) {
      return "500px";
    }
    if (isScreenSmOrLarger) {
      return "300px";
    }
    if (isScreenSmOrSmaller) {
      return "100%";
    }
  }, [isScreenSmOrLarger, isScreenSmOrSmaller, isScreenXlOrLarger]);

  const placeHolder = useMemo(() => {
    return tableType === TableType.projectMarkups ? "Search annotations" : "Search";
  }, [tableType]);

  function onClickAway(): void {
    if (isTextFieldFocused) {
      if (searchText.length === 0) {
        setShouldShowSearchBox(false);
      } else {
        trackEvent({
          name: `filter ${tableType}`,
          props: {
            filterBy: "filter search",
            textLength: searchText.length,
          },
        });
      }
      setIsTextFieldFocused(false);
    }
  }

  function onClearClicked(): void {
    setShouldShowSearchBox(false);
    setSearchText("");
  }

  // Clear the search text when the event is emitted from another component
  useEffect(() => {
    onClearTableSearch.on(onClearClicked);
  }, []);

  /**
   * Show search box in below scenario
   * - If shouldShowSearchBox is true
   * - Always show in extra small screens
   */
  if (shouldShowSearchBox || isExtraSmall) {
    return (
      <ClickAwayListener onClickAway={onClickAway}>
        <Box component="div">
          <SearchTextField
            placeholder={placeHolder}
            searchText={searchText}
            onClearClicked={onClearClicked}
            onChangeSearch={(text) => {
              setIsTextFieldFocused(true);
              setSearchText(text);
            }}
            sx={{ width: searchWidth }}
          />
        </Box>
      </ClickAwayListener>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      onClick={() => setShouldShowSearchBox(true)}
      sx={{
        cursor: "pointer",
        p: "6px 10px",
        "&:hover": {
          backgroundColor: addTransparency({
            color: sphereColors.gray500,
            alpha: EDecimalToHex.twentySix,
          }),
          color: sphereColors.blue500,
        },
      }}
    >
      <SvgIcon
        inheritViewBox
        component={SearchIcon}
        sx={{ width: "18px", height: "18px" }}
      />
      <Typography
        sx={{
          fontSize: DEFAULT_TEXT_FONT_SIZE,
          fontWeight: 700,
          ml: "8px",
        }}
      >
        Search
      </Typography>
    </Stack>
  );
}
